import { logger } from './logger';
/**
 * Lazy Import with Retry & Stale Chunk Recovery
 *
 * Wraps React.lazy() to handle chunk load failures (404s) that occur when:
 * - A new deployment changes hashed filenames
 * - The user returns to a cached tab with stale JS references
 * - Network issues cause temporary fetch failures
 *
 * Recovery strategy:
 * 1. First failure: retry the import once (handles transient network errors)
 * 2. Second failure: force a page reload to fetch fresh index.html with new chunk references
 * 3. Use sessionStorage flag to prevent infinite reload loops
 */

import { lazy, ComponentType } from 'react';

const RELOAD_FLAG_PREFIX = 'chunk_reload_';
const RELOAD_FLAG_MAX_AGE_MS = 30_000; // 30 seconds - prevent reload loops

/**
 * Check if a reload was recently attempted for this session
 */
export function wasRecentlyReloaded(): boolean {
  const flag = sessionStorage.getItem(RELOAD_FLAG_PREFIX + 'timestamp');
  if (!flag) return false;

  const elapsed = Date.now() - Number(flag);
  if (elapsed > RELOAD_FLAG_MAX_AGE_MS) {
    sessionStorage.removeItem(RELOAD_FLAG_PREFIX + 'timestamp');
    return false;
  }

  return true;
}

/**
 * Mark that a chunk-failure reload was triggered
 */
export function markReload(): void {
  sessionStorage.setItem(RELOAD_FLAG_PREFIX + 'timestamp', Date.now().toString());
}

/**
 * Clear the reload flag (should be called after successful app bootstrap)
 */
export function clearChunkReloadFlag(): void {
  sessionStorage.removeItem(RELOAD_FLAG_PREFIX + 'timestamp');
}

/**
 * Detect if an error is a chunk/module load failure
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  const message = error.message.toLowerCase();
  const name = error.name.toLowerCase();

  return (
    // Vite dynamic import failures
    message.includes('failed to fetch dynamically imported module') ||
    // Webpack chunk failures
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    name === 'chunkloaderror' ||
    // Generic network-level failures on script/module fetch
    (message.includes('failed to fetch') && name === 'typeerror') ||
    // Safari-specific
    message.includes('importing a module script failed') ||
    // Stale deploy: SPA fallback returned HTML for a hashed JS/CSS URL
    message.includes('mime type of "text/html"') ||
    message.includes("mime type of 'text/html'") ||
    message.includes('module script') ||
    message.includes("unexpected token '<'") ||
    message.includes('unexpected token "<"')
  );
}

/**
 * React.lazy() wrapper with automatic retry and stale-chunk recovery.
 *
 * Usage:
 * ```ts
 * // Instead of:
 * const MyPage = React.lazy(() => import('./pages/MyPage'));
 *
 * // Use:
 * const MyPage = lazyWithRetry(() => import('./pages/MyPage'));
 * ```
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(() => retryImport(importFn));
}

/**
 * React.lazy() wrapper for named exports with retry and stale-chunk recovery.
 *
 * Usage:
 * ```ts
 * const AdminRoute = lazyWithRetryNamed(
 *   () => import('./components/auth/AdminRoute'),
 *   (m) => m.AdminRoute
 * );
 * ```
 */
export function lazyWithRetryNamed<M extends Record<string, any>, T extends ComponentType<any>>(
  importFn: () => Promise<M>,
  extractDefault: (mod: M) => T
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    const mod = await retryImport(importFn as unknown as () => Promise<{ default: any }>);
    // The retryImport returns { default: ... } but for named exports we get the full module
    // We need to handle both cases
    const component = extractDefault(mod as unknown as M);
    return { default: component };
  });
}

/**
 * Core retry logic for dynamic imports
 */
async function retryImport<T>(importFn: () => Promise<T>, retries: number = 1): Promise<T> {
  try {
    return await importFn();
  } catch (error) {
    if (!isChunkLoadError(error)) {
      // Not a chunk load error — rethrow immediately
      throw error;
    }

    if (retries > 0) {
      // Retry once (handles transient network glitches)
      logger.warn('[lazyWithRetry] Chunk load failed, retrying...', {
        error: error instanceof Error ? error.message : String(error),
        retriesLeft: retries,
      });

      // Small delay before retry
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return retryImport(importFn, retries - 1);
    }

    // No retries left — the chunk is genuinely missing (stale deployment)
    // Force a full page reload to fetch the new index.html with updated chunk references
    if (!wasRecentlyReloaded()) {
      logger.warn(
        '[lazyWithRetry] Chunk load failed after retry — reloading page to fetch new assets'
      );
      markReload();
      window.location.reload();

      // Return a never-resolving promise to prevent React from rendering stale state
      return new Promise(() => {});
    }

    // Already reloaded recently — don't loop. Let the error propagate to ErrorBoundary.
    logger.error(
      '[lazyWithRetry] Chunk load failed even after page reload — showing error boundary'
    );
    throw error;
  }
}
